import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrainOrb, Button, Card, EmptyState, GlassPill, Skeleton } from '../components/primitives';
import { useAuth } from '../contexts/AuthContext';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useWelfareFlags } from '../lib/queries/useWelfareFlags';
import { useBrainConversations } from '../lib/queries/useBrainConversations';
import { listRecentGrowthCheckins } from '../lib/growthCheckin';
import type { GrowthCheckinSummary } from '../lib/growthCheckin';
import './page-shell.css';
import './Reviews.css';

function relativeDate(iso: string): string {
  const date = new Date(iso);
  const days = Math.round((Date.now() - date.getTime()) / 86_400_000);
  if (days < 1) return 'today';
  if (days < 2) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return date.toLocaleDateString();
}

const CORNER_LABEL: Record<string, string> = {
  tech: 'Technical',
  phys: 'Physical',
  psych: 'Psychological',
  social: 'Social',
};

export function Reviews() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { team } = useActiveTeam();
  const { flags, state: flagsState, errorMessage: flagsError } = useWelfareFlags(team?.id);
  const {
    items: conversations,
    state: conversationsState,
    errorMessage: conversationsError,
  } = useBrainConversations(user?.id);
  const [items, setItems] = useState<GrowthCheckinSummary[] | null>(null);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await listRecentGrowthCheckins(user.id);
        if (!cancelled) setItems(list);
      } catch (err) {
        if (!cancelled) { console.warn('[reviews] threw', err); setItems([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  return (
    <main className="main page reviews-page">
      <header className="page-head reviews-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 3 · review &amp; feedback</span>
          <h1 className="page-title">Reviews</h1>
          <p className="page-sub">Step back. Brain reads what you write here on every turn that follows.</p>
        </div>
      </header>

      {flagsState === 'error' && (
        <section className="welfare-flags" aria-label="Welfare alerts">
          <Card variant="opaque">
            <p className="page-empty">
              Couldn't compute welfare flags.{flagsError ? ` (${flagsError})` : null}
            </p>
          </Card>
        </section>
      )}
      {flagsState === 'ok' && flags.length > 0 && (
        <section className="welfare-flags" aria-label="Welfare alerts">
          <div className="page-section-label welfare-flags__label">
            ⚠ Welfare flags · {flags.length} · coach-only
          </div>
          <ul className="welfare-flags__list">
            {flags.map((flag) => (
              <li key={flag.id}>
                <Link to={`/players/${flag.player_id}`} className="welfare-flag" data-kind={flag.kind}>
                  <Card variant="opaque">
                    <div className="welfare-flag__head">
                      <span className="welfare-flag__player">{flag.player_name ?? 'Player'}</span>
                      <GlassPill mono>
                        {flag.kind === 'velocity_drop' ? 'velocity drop' : 'psych-social low'}
                      </GlassPill>
                    </div>
                    <p className="welfare-flag__detail">
                      {flag.kind === 'velocity_drop' && flag.corner && (
                        <>
                          <strong>{CORNER_LABEL[flag.corner]}</strong> dropped{' '}
                          <strong>{Math.abs(flag.delta)}pts</strong> across the last fortnight ({flag.rating_count} ratings).
                        </>
                      )}
                      {flag.kind === 'psych_social_low' && (
                        <>
                          Psych-social score sitting at <strong>{flag.delta}/100</strong> — below the 40 floor.
                        </>
                      )}
                    </p>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Card variant="glass" className="reviews-cta">
        <div className="reviews-cta__body">
          <h2>Coach growth check-in</h2>
          <p>Four questions, three minutes. Becomes a Brain prior on every Brain turn for the next fortnight.</p>
          <GlassPill mono>4 questions · ~3 min</GlassPill>
        </div>
        <Link to="/reviews/check-in">
          <Button variant="primary" size="touchline">Start a check-in</Button>
        </Link>
      </Card>

      {conversationsState === 'error' ? (
        <section className="reviews-conversations" aria-label="Past Brain conversations">
          <div className="page-section-label">Past conversations</div>
          <Card variant="opaque">
            <p className="page-empty">
              Couldn't load past conversations.
              {conversationsError ? ` (${conversationsError})` : null}
            </p>
          </Card>
        </section>
      ) : conversations.length > 0 ? (
        <section className="reviews-conversations" aria-label="Past Brain conversations">
          <div className="page-section-label">Past conversations · {conversations.length}</div>
          <ul className="reviews-conversations__list">
            {conversations.map((c) => (
              <li key={c.id}>
                <Card variant="opaque">
                  <div className="reviews-conversations__head">
                    <span className="reviews-conversations__title">{c.title ?? 'Untitled conversation'}</span>
                    <span className="reviews-conversations__when">{relativeDate(c.last_message_at)}</span>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="reviews-recent" aria-label="Recent check-ins">
        <div className="page-section-label">Recent check-ins</div>
        {items === null && (
          <ul className="reviews-list" aria-label="Loading check-ins">
            {Array.from({ length: 3 }, (_, i) => (
              <li key={i}>
                <Card variant="glass">
                  <Skeleton variant="text" width="20%" height={11} />
                  <div style={{ height: 8 }} />
                  <Skeleton variant="text" width="100%" height={14} />
                  <div style={{ height: 4 }} />
                  <Skeleton variant="text" width="85%" height={14} />
                </Card>
              </li>
            ))}
          </ul>
        )}
        {items && items.length === 0 && (
          <EmptyState
            glyph="🪞"
            title="No check-ins yet"
            body="Your first check-in becomes the floor for every Brain answer — voice or type, takes 2 minutes."
            cta={{ label: 'Start a check-in', onClick: () => navigate('/reviews/check-in') }}
          />
        )}
        {items && items.length > 0 && (
          <ul className="reviews-list">
            {items.map((item) => (
              <li key={item.id}>
                <Card variant="glass">
                  <div className="reviews-card__head">
                    <span className="reviews-card__when">{relativeDate(item.created_at)}</span>
                  </div>
                  <pre className="reviews-card__body">{item.fact}</pre>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
